// Scoped presentation only. Concept's existing ProductForm handles inventory errors,
// batch addition and Mini-Cart refresh; this module never sends a cart request.
const selector = '[data-bilago-bundle-summary]';
// Preserve each preceding choice; reset only later options when a combination
// does not exist. Prefer an available SKU without inventing any option values.
export const resolveBundleVariant = (variants, choices, changedIndex) => {
  let candidates = variants;
  const selected = [...choices];
  for (let index = 0; index < selected.length; index += 1) {
    let matching = candidates.filter((variant) => variant.options?.[index] === selected[index]);
    if (!matching.length && index > changedIndex) {
      selected[index] = (candidates.find((variant) => variant.available) || candidates[0])?.options?.[index];
      matching = candidates.filter((variant) => variant.options?.[index] === selected[index]);
    }
    if (!matching.length) return null;
    candidates = matching;
  }
  return candidates.find((variant) => variant.available) || candidates[0] || null;
};
const pickerStates = new WeakMap();
const syncPicker = (picker, changedIndex = -1, notify = false) => {
  const state = pickerStates.get(picker);
  if (!state || !state.native.isConnected) return;
  const {native, variants, controls, card} = state;
  const variant = resolveBundleVariant(variants, controls.map((control) => control.value), changedIndex);
  const input = card.querySelector('input[name="bundles"]');
  if (!variant || !input) return;
  controls.forEach((control, index) => {
    control.value = variant.options[index];
    Array.from(control.options).forEach((option) => {
      const matches = variants.filter((item) => item.options?.[index] === option.value &&
        item.options.slice(0, index).every((value, previousIndex) => value === variant.options[previousIndex]));
      option.disabled = !matches.length;
      option.textContent = option.value + (matches.length && !matches.some((item) => item.available) ? ' — Sold out' : '');
    });
  });
  native.value = String(variant.id);
  input.disabled = !variant.available;
  if (!variant.available) input.checked = false;
  if (notify) native.dispatchEvent(new Event('change', {bubbles:true}));
  const price = card.querySelector('.price');
  if (price) {
    let status = card.querySelector('[data-bilago-option-status]');
    if (!status) {
      status = document.createElement('span');
      status.dataset.bilagoOptionStatus = '';
      status.className = 'text-sm';
      status.setAttribute('role', 'status');
      price.after(status);
    }
    status.textContent = variant.available ? '' : 'Sold out';
  }
};
const initPickers = () => {
  document.querySelectorAll('[data-bilago-option-picker]').forEach((picker) => {
    if (pickerStates.has(picker)) return;
    const native = document.getElementById(picker.dataset.nativePicker || '');
    const card = picker.closest('.product-bundle__info');
    const host = picker.querySelector('[data-bilago-option-controls]');
    const namesSource = picker.querySelector('[data-bilago-option-names]');
    const variantsSource = picker.querySelector('[data-bilago-option-variants]');
    if (!native || !card || !host || !namesSource || !variantsSource) return;
    let names, variants;
    try {
      names = JSON.parse(namesSource.textContent || '[]');
      variants = JSON.parse(variantsSource.textContent || '[]');
    } catch { return; }
    if (!Array.isArray(names) || !names.length || !Array.isArray(variants) || !variants.length) return;
    const selected = variants.find((variant) => String(variant.id) === native.value) || variants[0];
    const controls = names.map((name, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'bilago-bundle-option';
      const label = document.createElement('span');
      label.id = `${native.id}-option-${index}-label`;
      label.className = 'bilago-bundle-option-label';
      label.textContent = name;
      const control = document.createElement('select');
      control.id = `${native.id}-option-${index}`;
      control.className = 'select';
      control.dataset.bilagoOptionIndex = String(index);
      control.setAttribute('aria-labelledby', label.id);
      [...new Set(variants.map((variant) => variant.options?.[index]).filter((value) => value != null))].forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        control.append(option);
      });
      control.value = selected.options[index];
      const field = document.createElement('div');
      field.className = 'field';
      field.append(control);
      const arrow = native.parentElement?.querySelector('svg');
      if (arrow) field.append(arrow.cloneNode(true));
      wrapper.append(label, field);
      host.append(wrapper);
      return control;
    });
    pickerStates.set(picker, {native, variants, controls, card});
    picker.querySelector('[data-bilago-option-fallback]')?.setAttribute('hidden', '');
    native.closest('.field')?.setAttribute('hidden', '');
    syncPicker(picker);
  });
};
const refresh = (root) => {
  const form = document.getElementById(root.dataset.form || '');
  const details = root.closest('details');
  const total = root.querySelector('[data-bilago-total]');
  const note = root.querySelector('[data-bilago-note]');
  const button = root.querySelector('[data-bilago-add]');
  const source = root.querySelector('[data-bilago-main-variants]');
  if (!form || !details || !total || !note || !button || !source) return;
  let variants;
  try { variants = JSON.parse(source.textContent || '[]'); } catch { return; }
  const variantId = form.elements.namedItem('id')?.value;
  const variant = variants.find((item) => String(item.id) === String(variantId));
  const quantity = Number(form.elements.namedItem('quantity')?.value || 1);
  const primary = form.querySelector('[type="submit"]');
  const selected = Array.from(details.querySelectorAll('input[name="bundles"]:checked:not(:disabled)'));
  let cents = Number(variant?.price || 0) * (Number.isFinite(quantity) ? quantity : 1);
  let pairedSavings = 0;
  const eligibleIds = (root.dataset.discountVariants || '').split(',');
  // Preview one eligible add-on without changing checkbox or cart state.
  const previewInput = !selected.length && variant?.available
    ? Array.from(details.querySelectorAll('input[name="bundles"]:not(:disabled)')).find((input) => {
      const option = input.closest('.product-bundle__info')?.querySelector('select:not([data-bilago-option-index])')?.selectedOptions?.[0];
      return eligibleIds.includes(String(option?.value || input.value));
    })
    : null;
  const displayed = selected.length ? selected : (previewInput ? [previewInput] : []);
  displayed.forEach((input) => {
    const option = input.closest('.product-bundle__info')?.querySelector('select:not([data-bilago-option-index])')?.selectedOptions?.[0];
    const price = Number(option?.dataset.price || input.dataset.price || 0);
    const count = Number(option?.dataset.min || input.dataset.min || 1);
    cents += price * count;
    if (eligibleIds.includes(String(option?.value || input.value))) {
      const pairs = Math.max(0, Math.min(quantity, count));
      pairedSavings += (Math.round(Number(variant?.price || 0) * 0.05) + Math.round(price * 0.05)) * pairs;
    }
  });
  try {
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:root.dataset.currency || 'USD',currencyDisplay:'narrowSymbol'});
    total.textContent = money.format((cents - pairedSavings) / 100);
    const original = root.querySelector('[data-bilago-original]');
    const savings = root.querySelector('[data-bilago-savings]');
    if (original) { original.hidden = !pairedSavings; original.textContent = money.format(cents / 100); }
    if (savings) { savings.hidden = !pairedSavings; savings.textContent = `SAVE ${money.format(pairedSavings / 100)}`; }
  } catch { return; }
  note.textContent = '';
  note.hidden = true;
  const busy = primary?.getAttribute('aria-busy') === 'true';
  button.disabled = !variant?.available || !selected.length || !primary || primary.disabled || primary.getAttribute('aria-disabled') === 'true' || busy;
  button.textContent = busy ? 'Adding…' : 'SAVE MORE! 🔥';
};
const refreshAll = () => document.querySelectorAll(selector).forEach(refresh);
const init = () => {
  initPickers();
  document.querySelectorAll(selector).forEach((root) => {
    if (root.dataset.bound) return;
    root.dataset.bound = 'true';
    const form = document.getElementById(root.dataset.form || '');
    if (form) {
      const observer = new MutationObserver(() => refresh(root));
      observer.observe(form, {attributes:true,subtree:true,attributeFilter:['disabled','aria-disabled','aria-busy','value']});
    }
    refresh(root);
  });
  refreshAll();
};
document.addEventListener('change', (event) => {
  const target = event.target;
  if (target instanceof Element && target.matches('select[data-bilago-option-index]')) {
    const picker = target.closest('[data-bilago-option-picker]');
    if (picker) {
      // The native label also listens for SELECT changes. Give it only the
      // resolved variant ID, never a raw option such as "White" or "DIN".
      event.stopPropagation();
      syncPicker(picker, Number(target.dataset.bilagoOptionIndex), true);
    }
  }
  queueMicrotask(refreshAll);
}, true);
document.addEventListener('input', () => queueMicrotask(refreshAll), true);
document.addEventListener('variant:change', () => queueMicrotask(init), true);
document.addEventListener('bundle:change', () => queueMicrotask(refreshAll), true);
document.addEventListener('shopify:section:load', init);
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-bilago-add]');
  const root = button?.closest(selector);
  if (!root || button.disabled) return;
  const form = document.getElementById(root.dataset.form || '');
  const primary = form?.querySelector('[type="submit"]');
  if (!form || !primary || primary.disabled || primary.getAttribute('aria-disabled') === 'true') return;
  if (form.reportValidity()) form.requestSubmit(primary);
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
else init();

// Empty bundle configuration: reuse Concept's ProductForm request and errors.
const buyNowPending = new Map();
const refreshBuyNow = (root) => {
  const form = document.getElementById(root.dataset.form || '');
  const primary = form?.querySelector('[type="submit"]');
  const button = root.querySelector('[data-bilago-buy-now-button]');
  if (!form || !primary || !button) return;
  const busy = primary.getAttribute('aria-busy') === 'true';
  button.disabled = primary.disabled || primary.getAttribute('aria-disabled') === 'true' || busy;
  button.textContent = busy ? 'Adding…' : 'Buy it now 🔥';
  if (!busy) buyNowPending.delete(root);
};
const initBuyNow = () => {
  document.querySelectorAll('[data-bilago-buy-now]').forEach((root) => {
    const form = document.getElementById(root.dataset.form || '');
    if (!form) return;
    if (!root.dataset.bound) {
      root.dataset.bound = 'true';
      new MutationObserver(() => refreshBuyNow(root)).observe(form, {
        attributes: true, subtree: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy']
      });
    }
    refreshBuyNow(root);
  });
};
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-bilago-buy-now-button]');
  const root = button?.closest('[data-bilago-buy-now]');
  if (!root || button.disabled) return;
  const form = document.getElementById(root.dataset.form || '');
  const primary = form?.querySelector('[type="submit"]');
  if (!form || !primary || primary.disabled || primary.getAttribute('aria-disabled') === 'true' || !form.reportValidity()) return;
  buyNowPending.set(root, String(form.elements.namedItem('id')?.value || ''));
  form.requestSubmit(primary);
});
document.addEventListener('ajaxProduct:added', (event) => {
  const added = event.detail?.product;
  const items = added?.items || (added ? [added] : []);
  buyNowPending.forEach((variantId, root) => {
    if (!root.isConnected || !items.some((item) => String(item.variant_id || item.id) === variantId)) return;
    buyNowPending.delete(root);
    const checkout = root.dataset.checkout;
    if (checkout?.startsWith('/') && !checkout.startsWith('//')) window.location.assign(checkout);
  });
});
document.addEventListener('ajaxProduct:error', () => buyNowPending.clear());
document.addEventListener('variant:change', () => queueMicrotask(initBuyNow), true);
document.addEventListener('shopify:section:load', initBuyNow);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initBuyNow, {once:true});
else initBuyNow();
